import { __awaiter } from 'tslib';
import SecuritySDK, { convertBase64ToString } from '@byted/uc-secure-sdk';
import { ErrorType } from '../consts.js';
import { BaseError } from '../errors/BaseError.js';
import { Logger } from '../metrics/logger.js';
import { Monitor, MonitorItem } from '../metrics/monitor.js';
import { ConfigKeys } from '../plugin/DbProxy.js';
import { ContextClassBase, ServiceIdentity } from '../service/RuntimeContext.js';
import { im_proto } from '../../proto/index.js';

class AuthManager extends ContextClassBase {
    constructor(ctx) {
        super(ctx);
        if (this.isCertAuth) {
            this.securitySdk = new SecuritySDK({ aid: this.ctx.option.appId });
        }
    }
    get isCertAuth() {
        return this.ctx.option.authType === im_proto.AuthType.CERT_AUTH;
    }
    prepareToken() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const cachedToken = yield ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.loadConfig(ConfigKeys.UserToken));
            if (cachedToken && !(this.isCertAuth && this.getCert() === false)) {
                this.ctx.cachedToken = cachedToken;
            }
            else {
                yield this.refreshToken();
            }
        });
    }
    refreshToken() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const getToken = this.ctx.option.token;
            if (typeof getToken === 'function') {
                const startTime = Monitor.performanceNow();
                let token = null;
                let tokenPromise;
                if (this.isCertAuth) {
                    token = yield this.handleCertAuthToken(getToken);
                }
                else {
                    tokenPromise = getToken();
                    if (tokenPromise instanceof Promise) {
                        token = yield tokenPromise;
                    }
                    else {
                        token = tokenPromise;
                    }
                }
                if (token !== null) {
                    this.ctx.cachedToken = token;
                    yield ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.saveConfig(ConfigKeys.UserToken, token));
                    this.resolve(ServiceIdentity.Monitor).emitDuration(MonitorItem.BizRefreshToken, startTime);
                    Logger.ctxDebug(this.ctx, 'token cached from function: ', this.ctx.cachedToken);
                    return token;
                }
                else {
                    throw new BaseError({
                        ctx: this.ctx,
                        msg: 'token is null',
                        type: ErrorType.TokenFuncError,
                        sender: this
                    });
                }
            }
            else {
                this.ctx.cachedToken = getToken;
                Logger.ctxDebug(this.ctx, 'token cached from const: ', this.ctx.cachedToken);
                yield ((_b = this.resolve(ServiceIdentity.DbProxy)) === null || _b === void 0 ? void 0 : _b.saveConfig(ConfigKeys.UserToken, this.ctx.cachedToken));
                return getToken;
            }
        });
    }
    sign(reqData) {
        const signBase64 = this.securitySdk.cryptoSDK.sign(reqData);
        const signString = convertBase64ToString(signBase64);
        try {
            return JSON.parse(signString);
        }
        catch (err) {
            throw new BaseError({
                ctx: this.ctx,
                msg: 'Failed to JSON.parse signString',
                type: ErrorType.AuthSignError,
                sender: this
            });
        }
    }
    getCert() {
        return this.securitySdk.cryptoSDK.getCertificateV2();
    }
    getTsSign() {
        return this.securitySdk.cryptoSDK.getTsSign();
    }
    handleCertAuthToken(getToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const certificate = yield this.securitySdk.cryptoSDK.getCertSignRequest();
            const { tsSign, token, sdkCert } = yield getToken(certificate);
            const signParams = {
                ts_sign: tsSign,
                ticket: token,
                client_cert: sdkCert
            };
            this.securitySdk.cryptoSDK.setSignValue(signParams);
            return token;
        });
    }
}

export { AuthManager };
//# sourceMappingURL=AuthManager.js.map
